'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2 } from 'lucide-react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { signIn } from 'next-auth/react'
import { useTransition } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import * as z from 'zod'

import { Button } from '../../../_components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../../_components/ui/card'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '../../../_components/ui/form'
import { Input } from '../../../_components/ui/input'

const loginFormSchema = z.object({
  email: z
    .string({
      required_error: 'O e-mail é obrigatório',
    })
    .email({
      message: 'Insira um e-mail válido.',
    }),
  password: z.string({
    required_error: 'A senha é obrigatória',
  }),
})

type LoginForm = z.infer<typeof loginFormSchema>

const LoginScene = () => {
  const [isPending, startTransition] = useTransition()

  const router = useRouter()

  const methods = useForm<LoginForm>({
    resolver: zodResolver(loginFormSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  })

  const onSubmit = async (data: LoginForm) => {
    startTransition(async () => {
      router.prefetch('/dashboard')

      const response = await signIn('admin', {
        email: data.email,
        password: data.password,
        redirect: false,
      })
      if (!response?.ok) {
        console.error(response?.error)
        toast.error('Erro ao fazer login', {
          description: 'Verifique suas credenciais e tente novamente.',
        })
        return
      }

      router.refresh()
    })
  }

  return (
    <div className="flex h-screen w-full items-center justify-center bg-primary">
      <div className="flex h-full w-full items-center justify-center bg-primary">
        <Card>
          <CardHeader>
            <CardTitle>Login</CardTitle>
            <CardDescription>
              Faça login para acessar nosso sistema.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...methods}>
              <form
                onSubmit={methods.handleSubmit((data) => onSubmit(data))}
                className="flex flex-col gap-4"
              >
                <FormField
                  control={methods.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input placeholder="E-mail" {...field} />
                      </FormControl>

                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={methods.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input placeholder="Senha" type="password" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button type="submit" disabled={isPending}>
                  {isPending && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Entrar
                </Button>

                <Button asChild variant="link">
                  <Link href="/dashboard/login/forgot-password">
                    Esqueci minha senha
                  </Link>
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default LoginScene
